import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import PropTypes from 'prop-types';

function EditUser(props) {
    const [open, setOpen] = useState(false);
    const [user, setUser] = useState({});
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingRoleChange, setPendingRoleChange] = useState('');

    const handleClickOpen = () => {
        if (!props.data || !props.data.row) {
            console.error("No user data provided");
            return;
        }

        const userData = props.data.row; // Get the user data
        const initializedUser = Object.keys(userData).reduce((acc, key) => {
            if (key !== '_links') {
                acc[key] = userData[key] !== undefined ? userData[key] : null;
            }
            return acc;
        }, {});

        setUser(initializedUser);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;

        // Check if the role is changing
        if (name === "role" && value !== user.role) {
            setPendingRoleChange(value);
            setConfirmOpen(true); // Open confirmation dialog
        } else {
            setUser({
                ...user,
                [name]: value
            });
        }
    };

    const handleConfirmChange = () => {
        // Update the user role and close the confirmation dialog
        setUser({
            ...user,
            role: pendingRoleChange
        });
        setConfirmOpen(false);
    };

    const handleCancelConfirm = () => {
        setConfirmOpen(false);
    };

    const handleSave = () => {
        props.updateUser(user, props.data.row.id); // Use the self link for updates
        handleClose();
    };

    return (
        <div>
            <IconButton onClick={handleClickOpen}>
                <EditIcon color="primary" />
            </IconButton>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>Edit User</DialogTitle>
                <DialogContent sx={{ width: '800px' }}>
                    <Stack spacing={2} mt={1}>
                        {/* ID Field */}
                        <TextField
                            label="ID"
                            name="id"
                            value={user.id || ''}
                            disabled // Make ID uneditable
                            fullWidth
                        />
                        {/* Username Field */}
                        <TextField
                            label="Username"
                            name="username"
                            value={user.username || ''}
                            disabled // Make username uneditable
                            fullWidth
                        />

                        {/* Role Select */}
                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select
                                name="role"
                                value={user.role || ''}
                                onChange={handleChange}
                                label="Role"
                            >
                                {props.userRole === 'ROLE_ADMIN' 
                                    ? [
                                        <MenuItem key="admin" value="ADMIN">ADMIN</MenuItem>,
                                        <MenuItem key="staff" value="STAFF">STAFF</MenuItem>,
                                        <MenuItem key="student" value="STUDENT">STUDENT</MenuItem>
                                      ]
                                    : [<MenuItem key="student" value="STUDENT">STUDENT</MenuItem>]
                                }
                            </Select>
                        </FormControl>

                        {/* Other Fields */}
                        {Object.keys(user).map((key) => (
                            key !== 'role' && key !== 'id' && key !== 'username' && key !== 'activationStatus' && (
                                <TextField
                                    key={key}
                                    label={key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                                    name={key}
                                    value={user[key] || ''}
                                    onChange={handleChange}
                                    multiline={key === "notes"}
                                    rows={key === "notes" ? 4 : 1}
                                    fullWidth
                                    disabled
                                />
                            )
                        ))}

                        {/* Activation Status Select */}
                        <FormControl fullWidth>
                            <InputLabel>Activation Status</InputLabel>
                            <Select
                                name="activationStatus"
                                value={user.activationStatus ?? ''}
                                onChange={handleChange}
                                label="Activation Status"
                            >
                                <MenuItem key="true" value={true}>Active</MenuItem>
                                <MenuItem key="false" value={false}>Inactive</MenuItem>
                            </Select>
                        </FormControl>

                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSave}
                        variant="contained"
                        color="primary"
                    >Save</Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog open={confirmOpen} onClose={handleCancelConfirm}>
                <DialogTitle>Confirm Role Change</DialogTitle>
                <DialogContent>
                    Are you sure you want to make this user a {pendingRoleChange}?
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelConfirm}>Cancel</Button>
                    <Button onClick={handleConfirmChange} color="primary">Confirm</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

EditUser.propTypes = {
    data: PropTypes.shape({
        row: PropTypes.object.isRequired
    }).isRequired,
    updateUser: PropTypes.func.isRequired,
    userRole: PropTypes.string.isRequired
};

export default EditUser;
